import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import { Field, Form, Formik } from 'formik';
import MaterialTable, { Column } from 'material-table';
import Image from 'material-ui-image';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { firestore } from '../../firebase/firebase';
import { User } from '../../models/User';
import { getUsers } from '../../store/actions/users';
import { UserState } from '../../store/reducers/user';

interface RootState {
  user: UserState;
}

const Users: React.FC = () => {
  const userState = useSelector((state: RootState) => state.user);
  const theme = useTheme();
  const history = useHistory();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [
    showAccountVerificationDialog,
    setShowAccountVerificationDialog,
  ] = useState(false);

  const [showReasonDialog, setShowReasonDialog] = useState(false);

  const [verifyingUserAccount, setVerifyingUserAccount] = useState<{
    uid: string;
    icImageUrl: string;
    selfieImageUrl: string;
  }>();

  const [confirmedUser, setConfirmedUser] = useState<{
    uid: string;
    name: string;
    ic: string | undefined;
  }>();

  const columns: Column<User>[] = [
    { title: 'Name', field: 'name', filtering: false, searchable: true },
    { title: 'IC No.', field: 'icNumber', filtering: false, searchable: true },
    {
      title: 'Contact Number',
      field: 'contactNumber',
      filtering: false,
      searchable: false,
    },
    { title: 'Email', field: 'email', filtering: false, searchable: false },
    {
      title: 'Account Verification',
      field: 'verificationStatus',
      lookup: {
        pending: 'pending',
        verified: 'verified',
        rejected: 'rejected',
        resubmitted: 'resubmitted',
      },
      searchable: false,
    },
    {
      title: 'Status',
      field: 'healthStatus',
      lookup: { green: 'green', yellow: 'yellow', red: 'red' },
      searchable: false,
    },
  ];

  const dispatch = useDispatch();

  useEffect(() => {
    let unsubsribe: any;
    const fetchUsers = async () => {
      try {
        unsubsribe = dispatch(getUsers());
      } catch (err) {}
    };
    fetchUsers();
    return () => {
      unsubsribe();
    };
  }, [dispatch]);

  const handleClose = () => {
    setShowConfirmDialog(false);
  };

  const handleCloseAccountVerificationDialog = () => {
    setShowAccountVerificationDialog(false);
  };

  const markRedHandler = () => {
    firestore
      .collection('users')
      .doc(confirmedUser?.uid)
      .update({
        healthStatus: 'red',
      })
      .then(function () {})
      .catch(function (error) {});
    setShowConfirmDialog(false);
  };

  const confirmCaseHandler = (event: any, rowData: any) => {
    setConfirmedUser({
      uid: rowData.uid,
      name: rowData.name,
      ic: rowData.icNumber,
    });
    setShowConfirmDialog(true);
  };

  const verifyAccountHandler = (event: any, rowData: any) => {
    setVerifyingUserAccount({
      uid: rowData.uid,
      icImageUrl: rowData.icImageUrl,
      selfieImageUrl: rowData.selfieImageUrl,
    });
    setShowAccountVerificationDialog(true);
  };

  const updateAccountStatusHandler = (
    accountStatus: 'rejected' | 'verified'
  ) => {
    setShowAccountVerificationDialog(false);

    if (accountStatus === 'rejected') {
      // console.log('show reject dialog');

      setShowReasonDialog(true);
    } else {
      firestore
        .collection('users')
        .doc(verifyingUserAccount?.uid)
        .update({
          verificationStatus: accountStatus,
          reason: null,
          reasonCode: null,
        })
        .then(function () {
          // console.log('Document successfully updated!');
        })
        .catch(function (error) {
          // console.error('Error updating document: ', error);
        });
    }
  };

  const submitReasonHandler = (code: string) => {
    setShowReasonDialog(false);

    let reasonDesc;

    switch (String(code)) {
      case '1':
        reasonDesc = 'Your IC attachment is wrong/unclear.';
        break;
      case '2':
        reasonDesc = 'Your listed IC number is incorrect.';
        break;
      case '3':
        reasonDesc = "Your Name isn't the same as per your Identity card.";
        break;
      case '4':
        reasonDesc =
          'Your Selfie photo is unclear/does not match with your Identity card.';
        break;
    }

    firestore
      .collection('users')
      .doc(verifyingUserAccount?.uid)
      .update({
        verificationStatus: 'rejected',
        reason: reasonDesc,
        reasonCode: code,
      })
      .then(function () {
        // console.log('Document successfully updated!');
      })
      .catch(function (error) {
        // console.error('Error updating document: ', error);
      });
  };

  const rowClickHandler = (
    event: React.MouseEvent<Element, MouseEvent> | undefined,
    rowData: User
  ) => {
    history.push('/user/' + rowData.uid);
  };

  const getPageSize = (): number => {
    if (userState.users.length < 5) {
      return 5;
    } else if (userState.users.length < 10) {
      return 10;
    } else if (userState.users.length < 15) {
      return 15;
    } else {
      return 20;
    }
  };

  let confirmDesc = `Are you sure want to mark ${confirmedUser?.name} (${confirmedUser?.ic}) as confirmed case?`;

  let images = null;

  if (verifyingUserAccount) {
    images = (
      <Grid container spacing={2} alignItems="center" justify="center">
        <Grid item xs={6}>
          <Image aspectRatio={1.79} src={verifyingUserAccount!!.icImageUrl} />
        </Grid>
        <Grid item xs={6}>
          <Image aspectRatio={1} src={verifyingUserAccount!!.selfieImageUrl} />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <MaterialTable
        key={userState.users.length}
        localization={{
          toolbar: { searchPlaceholder: 'Search Name / IC No.' },
        }}
        options={{
          search: true,
          searchFieldAlignment: 'right',
          filtering: true,
          pageSize: getPageSize(),
          pageSizeOptions: [],
          actionsColumnIndex: -1,
        }}
        columns={columns}
        data={userState.users}
        title="Users"
        onRowClick={(event, rowData) => rowClickHandler(event, rowData!!)}
        actions={[
          (rowData) => ({
            icon: () => <CheckCircleIcon />,
            tooltip: 'Verify Account',
            onClick: verifyAccountHandler,
            disabled: rowData.verificationStatus === undefined,
          }),
          (rowData) => ({
            icon: () => <ErrorIcon />,
            tooltip: 'Mark',
            onClick: confirmCaseHandler,
            disabled: rowData.healthStatus === 'red',
          }),
        ]}
      />

      <Dialog
        open={showConfirmDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Mark as Confirmed Case?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDesc}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            No
          </Button>
          <Button onClick={markRedHandler} color="primary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth="xl"
        open={showAccountVerificationDialog}
        onClose={handleCloseAccountVerificationDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Verifying User Account'}
        </DialogTitle>
        <DialogContent>{images}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => updateAccountStatusHandler('rejected')}
            color="primary"
          >
            Reject
          </Button>
          <Button
            onClick={() => updateAccountStatusHandler('verified')}
            color="primary"
            autoFocus
          >
            Verified
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReasonDialog}
        fullWidth={true}
        onClose={() => setShowReasonDialog(false)}
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Reason</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={{
              reason: '',
            }}
            validationSchema={yup.object({
              reason: yup.string().required('This field cannot be empty'),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              submitReasonHandler(values.reason);
            }}
          >
            {({ values, errors, isSubmitting, dirty, isValid }) => (
              <Form>
                <Typography
                  variant="h6"
                  component="h6"
                  style={{ marginBottom: theme.spacing(2) }}
                >
                  Send Notification
                </Typography>

                <InputLabel id="select-label">Reason</InputLabel>
                <Field
                  labelId="select-label"
                  as={Select}
                  name="reason"
                  fullWidth={true}
                  error={errors.reason}
                >
                  <MenuItem value={1}>
                    Your IC attachment is wrong/unclear.
                  </MenuItem>
                  <MenuItem value={2}>
                    Your listed IC number is incorrect.
                  </MenuItem>
                  <MenuItem value={3}>
                    Your Name isn't the same as per your Identity card.
                  </MenuItem>
                  <MenuItem value={4}>
                    Your Selfie photo is unclear/does not match with your
                    Identity card.
                  </MenuItem>
                </Field>
                <FormHelperText error={errors.reason !== null}>
                  {errors.reason}
                </FormHelperText>

                <DialogActions>
                  <Button
                    disabled={!(isValid && dirty)}
                    variant="text"
                    type="submit"
                    color="primary"
                    style={{ marginTop: '16px' }}
                  >
                    Submit
                  </Button>
                </DialogActions>

                {/* <pre>{JSON.stringify(values, null, 2)}</pre>
                <pre>{JSON.stringify(errors, null, 2)}</pre> */}
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Users;
